import { keysOf } from '@upright/common/utils';
import { mapValues, pickBy } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export function useRouteParams<
  T extends {
    [K in keyof T]?: string;
  } & { name?: string },
>(): T {
  // OA: React Router v6 added a param "*" here, which we don't want to use in our param getters/setters -->
  // hence the pickBy to get rid of it.
  const params = pickBy(useParams<T>(), (_value, key) => key !== '*') as T;
  const decoded = mapValues(params, p => (p !== undefined ? decodeAndHandlePercentSign(p) : undefined));
  const ret = { ...decoded, name: replaceWrongNameParamWithLoading((decoded as T & { name?: string }).name) };
  // OA: This return type should be Partial<T>, but I didn't want to open the rabbit hole of refactoring all the types.
  return ret as T;
}

/**
 * OA COMMENT 9/2022: After update to React Router v6, components using this stuff seem to work,
 * but I haven't actually checked if the behavior is still the same. We may run into issues that need
 * repairing with some edge cases, but we'll see.
 *
 * React router v5 decodes percentage signs automatically, but *only* percentage signs.
 * This makes it tricky for route param values (like company names) that may contain percentages,
 * because the percentages are already decoded at this stage, but any other unicode characters are not.
 * Throwing an already-decoded percentage into decodeURIComponent will result in an error complaining about
 * a malformed URI.
 *
 * This function is a fix that splits the string by percentage signs and then checks if the two characters
 * following each percentage are a hex number. If yes, they probably are a unicode character that should be decoded.
 * If not, the percentage is most likely an intended percentage sign that has already been decoded.
 *
 * This fix likely works for most cases. If the param value contains an intended percentage sign followed by some characters
 * that can be interpreted as a hexadecimal number, we'll get some funny results. As of 1/2021, this can only happen in parsing
 * names of companies/funds etc., and those are anyway quickly replaced by values coming from the server, so no biggie.
 */

function decodeAndHandlePercentSign(param: string) {
  try {
    const decoded = decodeURIComponent(param);
    return decoded;
  } catch (e) {
    const splitByPercent = param.split('%');
    const wholeWordBeginsWithPctSign = param[0] === '%';
    const handled = splitByPercent.map((part, index) => {
      // This is needed to handle a company name that starts with two numbers (or some other
      // combination that can be interpreted as a hex number)
      const isFirstPartAndWithoutPctSign = index === 0 && !wholeWordBeginsWithPctSign;

      const firstTwoChars = part.substring(0, 2);
      const canBeDecoded = !isFirstPartAndWithoutPctSign && isHexNumber(firstTwoChars);
      if (canBeDecoded) {
        return `${decodeURIComponent(`%${firstTwoChars}`)}${part.substring(2)}`;
      }
      return isFirstPartAndWithoutPctSign ? part : `%${part}`;
    });
    return handled.join('');
  }
}

function isHexNumber(string: string) {
  const convertedToInt = parseInt(string, 16);
  const convertedBackToString = convertedToInt.toString(16);
  return string.toLowerCase() === convertedBackToString.toLowerCase();
}

// OA: Replaces leagcy names as well as passes "Loading..." if no name param is given.
// OA comment 8/2022: It's somewhat wonky to have the loading text placeholder come from here.
// it should be handled by the rendering location instead. But won't refactor now.
// OA comment 9/2022: I tried to refactor this away, but it seems that entity views crash if the name
// param is falsy --> decided to not spend time on that now.
const loadingPlaceholder = 'Loading...';
function replaceWrongNameParamWithLoading(param: string | undefined) {
  // OA: Some legacy URLs might have these
  const namesToReplace = ['i', 'todo'];
  if (!param || namesToReplace.includes(param.toLowerCase())) {
    return loadingPlaceholder;
  }
  return param;
}

export function useGetRouteParamsSetter<T extends { [K in keyof T]: T[K] }>(currentParams: T) {
  const location = useLocation();
  const navigate = useNavigate();

  function setRouteParams(newParams: T) {
    const routeParamValuesFromUrl = location.pathname.split('/').map(decodeAndHandlePercentSign);
    const paramKeys = keysOf(newParams);
    const updatedParams = routeParamValuesFromUrl.map((value, index) => {
      if (index === 0 || index === 1) {
        // Index 0 is always an empty string and index 1 contains the first subpage location such as 'company', 'portfolio' etc.
        return value;
      }
      const matchingKey = paramKeys[index - 2];
      const matchingValue: T[keyof T] | undefined = currentParams[matchingKey];
      if (matchingValue && matchingValue !== loadingPlaceholder && matchingValue !== value) {
        throw Error(
          `Mismatching route params at key ${matchingKey.toString()}, value from url: "${value}", value from params: "${
            matchingValue as string
          }"`,
        );
      }
      const newValue = newParams[matchingKey];
      return encodeURIComponent(newValue);
    });
    const newPathname = updatedParams.join('/');
    const newUrl = newPathname + location.search;
    navigate(newUrl, { replace: true });
  }
  return setRouteParams;
}
